import { HeartIcon, BrainIcon, DoorClosedIcon as LockClosedIcon, TrashIcon, UserIcon, Share2Icon, UserPlusIcon, FlameIcon, Coins } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Snap } from '@/types/database.types';
import ShareModal from '@/components/ShareModal';
import InviteModal from '@/components/InviteModal';
import { useState } from 'react';
import FollowButton from '@/components/profile/FollowButton';
import CategoryTags from '@/components/CategoryTags';
import AnimatedLogo from '@/components/AnimatedLogo';
import DeleteModal from '@/components/DeleteModal';

interface SnapHeaderProps {
  snap: Snap;
  onLike: () => void;
  onAskAi: () => void;
  onClose: () => void;
  onDelete: () => void;
  isSnapClosed: boolean;
}

const DEFAULT_AVATAR = 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png?20150327203541';

export default function SnapHeader({
  snap,
  onLike,
  onAskAi,
  onClose,
  onDelete,
  isSnapClosed
}: SnapHeaderProps) {
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  // Initialize avatar URL when component mounts or snap changes
  useState(() => {
    if (snap.creator?.avatar_url) {
      setAvatarUrl(snap.creator.avatar_url);
    } else {
      setAvatarUrl(DEFAULT_AVATAR);
    }
  });

  const handleImageError = () => {
    setImageError(true);
    setAvatarUrl(DEFAULT_AVATAR);
  };

  // Reset image error state when snap or avatar URL changes
  if (snap.creator?.avatar_url && !imageError && avatarUrl !== snap.creator.avatar_url) {
    setAvatarUrl(snap.creator.avatar_url);
    setImageError(false);
  }

  const handleClose = () => {
    setShowCloseModal(true);
  };

  const confirmClose = () => {
    onClose();
    setShowCloseModal(false);
  };

  return (
    <div className="relative">
      <div className="absolute inset-0 bg-gradient-to-r from-blue-200 to-blue-300" />
      
      <div className="relative px-4 sm:px-6 py-6 sm:py-8">
        <div className="flex flex-col space-y-6">
          {/* Avatar and Action Buttons Row */}
          <div className="flex items-center">
            {snap.is_system ? (
              <AnimatedLogo size="md" />
            ) : avatarUrl ? (
              <img
                src={avatarUrl}
                alt={snap.is_anonymous ? 'Anonymous' : (snap.creator?.username || 'Unknown')}
                className="h-12 w-12 rounded-full object-cover ring-2 ring-white/30"
                onError={handleImageError}
              />
            ) : (
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center ring-2 ring-white/30">
                <UserIcon className="h-6 w-6 text-blue-900" />
              </div>
            )}
            
            {/* CashSnap Badge */}
            {snap.cashsnap && (
              <div className="ml-3 relative">
                <img 
                  src="/cashsnap_min.png" 
                  alt="CashSnap" 
                  className="h-12 w-12 animate-pulse" 
                  style={{
                    animation: 'pulse 2s infinite ease-in-out'
                  }}
                />
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {!snap.isOwner && snap.creator && (
              <FollowButton
                userId={snap.creator.id}
                isFollowing={snap.creator.is_following}
                size="sm"
              />
            )}

            <button
              onClick={onLike}
              className={`inline-flex items-center px-3 py-1.5 border border-blue-900 rounded-md text-sm font-medium transition-colors ${
                snap.userLiked 
                  ? 'bg-blue-900 text-white' 
                  : 'text-blue-900 hover:bg-blue-900 hover:text-white'
              }`}
              aria-label={snap.userLiked ? 'Unlike' : 'Like'}
              title={snap.userLiked ? 'Unlike' : 'Like'}
            >
              <HeartIcon 
                className={`h-4 w-4 mr-1 ${snap.userLiked ? 'fill-white' : ''}`}
              />
              <span className="leading-none">{snap.likes_count}</span>
            </button>

            {/* Show Share button for public SNAPs */}
            {!snap.is_private && (
              <button
                onClick={() => setShowShareModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-blue-900 rounded-md text-sm font-medium text-blue-900 hover:bg-blue-900 hover:text-white transition-colors"
              >
                <Share2Icon className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline leading-none">Share</span>
              </button>
            )}

            {/* Show Invite button for private SNAPs */}
            {snap.is_private && snap.isOwner && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="inline-flex items-center px-3 py-1.5 border border-blue-900 rounded-md text-sm font-medium text-blue-900 hover:bg-blue-900 hover:text-white transition-colors"
              >
                <UserPlusIcon className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline leading-none">Invite Users</span>
              </button>
            )}

            {snap.isOwner && (
              <>
                <button
                  onClick={onAskAi}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-900 rounded-md text-sm font-medium text-blue-900 hover:bg-blue-900 hover:text-white transition-colors"
                >
                  <BrainIcon className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline leading-none">Ask AI</span>
                </button>
                <button
                  onClick={handleClose}
                  disabled={isSnapClosed}
                  className="inline-flex items-center px-3 py-1.5 border border-blue-900 rounded-md text-sm font-medium text-blue-900 hover:bg-blue-900 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <LockClosedIcon className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline leading-none">Close SNAP</span>
                </button>
                <button
                  onClick={onDelete}
                  className="inline-flex items-center px-3 py-1.5 border border-red-600 rounded-md text-sm font-medium text-red-600 hover:bg-red-600 hover:text-white transition-colors"
                >
                  <TrashIcon className="h-4 w-4 mr-1" />
                  <span className="hidden sm:inline leading-none">Delete</span>
                </button>
              </>
            )}
          </div>

          {/* User Info and Time */}
          <div className="flex flex-wrap items-center gap-4 text-blue-800 text-sm">
            <div className="flex items-center">
              {snap.is_system ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  <FlameIcon className="h-3.5 w-3.5 mr-1" />
                  FIRESNAP
                </span>
              ) : (
                <span>{snap.is_anonymous ? 'Anonymous' : snap.creator?.username}</span>
              )}
            </div>
            <div className="flex items-center">
              <span>
                {isSnapClosed ? 'Closed' : `Ends ${formatDistanceToNow(new Date(snap.ends_at), { addSuffix: true })}`}
              </span>
            </div>
            {snap.is_private && (
              <div className="flex items-center text-blue-900">
                <LockClosedIcon className="h-4 w-4 mr-1" />
                <span>Private SNAP</span>
              </div>
            )}
            {snap.cashsnap && (
              <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <Coins className="h-3.5 w-3.5 mr-1" />
                CASHSNAP
              </div>
            )}
          </div>

          {/* Title and Description */}
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">{snap.title}</h1>
            {snap.description && (
              <p className="text-blue-800 mt-2">{snap.description}</p>
            )}
            <CategoryTags categories={snap.categories} variant="white" className="mt-4" />
          </div>
        </div>
      </div>

      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        snapId={snap.id}
        snapTitle={snap.title}
      />

      <InviteModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        snapId={snap.id}
        snapTitle={snap.title}
      />

      <DeleteModal
        isOpen={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        onConfirm={confirmClose}
        title="Close SNAP"
        message="Are you sure you want to close this SNAP? Once closed, no more votes can be cast. This action cannot be undone."
      />
    </div>
  );
}